import { useEffect, useState, useMemo, useRef } from 'react';
import { Typography, Card, CardHeader, CardContent, Toolbar, Box } from '@mui/material';
import { Order, OrderStatus, SalesPoint, CustomerInfo, OrderItem } from 'orderme-api-integration-client';
import { NavBar } from '../appBars/navBar/navBar';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import { getOrderStatusLabel } from '../../helpers/orderStatus';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Label } from '../../controls';
import { OrderClient, SalePointClient } from '../../helpers/client';
import { RootState } from '../../redux/store';
import { useAppSelector } from '../../redux/hooks';
import {
  CancelPaymentButton,
  DetailDatesBox,
  DiscountTextTypography,
  HeaderText,
  InfoItemName,
  InfoItemValue,
  OrderDetailsContainer,
  ConfirmPaymentButton,
  Separator,
  UnpaidAreaContainer,
} from './orderDetailsStyles';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { appStyleMode } from '../../helpers/clientConfigs';
import { AppStyle } from '../../helpers/appStyle';
import useGetParameter from '../../hooks/useGetParameter';
import { RouterParam } from '../../helpers/routerParam';
import { assignCustomerDetails } from '../../helpers/customerHelper';
import { OrderItemsGroup } from './orderItemsGroup';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { OrderDetailRoute, OrderHistoryRoute } from '../../routes/allRoutes';
import OrderStatusStepper from './orderStatusStepper';
import OrderHelper from '../../helpers/orderHelper';
import { PaymentStatus } from '../payment/paymentEnum';
import { PaymentDialog } from '../payment/paymentDialog';

let inAppBrowserRef: any;

interface PhoneProps {
  phone: string;
}
function Phone(props: PhoneProps) {
  return <a href={`tel:${props.phone}`}>{props.phone}</a>;
}

export function OrderDetails() {
  const routeNavigator = useRouteNavigator();
  const orderId = useGetParameter(RouterParam.Id);
  const orderClient: OrderClient = useMemo(() => new OrderClient(), []);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const salePointClient: SalePointClient = useMemo(() => new SalePointClient(), []);

  const [isBusy, setIsBusy] = useState<boolean>(false);

  const [order, setOrder] = useState<Order>();
  const [orderServices, setOrderServices] = useState<OrderItem[]>();
  const [orderProducts, setOrderProducts] = useState<OrderItem[]>();
  const [destination, setDestination] = useState<SalesPoint>();
  const [isItemsOnly, setIsItemsOnly] = useState<boolean>();
  const { t } = useTranslation(['order']);
  const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const userInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);

  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>();
  const paymentTimeoutRef = useRef<number>(0);
  const exitListenerRef = useRef<(() => void) | null>(null);
  const loadStopListenerRef = useRef<((event: any) => void) | null>(null);

  useEffect(() => {
    if (orderId) {
      orderClient
        .get2(orderId, userInfo.id)
        .then((response) => {
          setOrder(response);
        })
        .catch((err) => {
          console.error(err);
          routeNavigator.goHome(true);
        });
    } else {
      routeNavigator.goHome(true);
    }
  }, [orderId, orderClient, userInfo.id, routeNavigator]);

  useEffect(() => {
    if (!order) {
      setDestination(null);
      setOrderServices([]);
      setOrderProducts([]);
      return;
    }

    setOrderServices(
      order.items.filter((item) => !isProduct(item)).sort((a, b) => (a.isPack === b.isPack ? 0 : a.isPack ? 1 : -1))
    );
    setOrderProducts(order.items.filter((item) => isProduct(item)));
    setIsItemsOnly(OrderHelper.isOnlyPhysicalItems(order));

    if (order.destination?.knownId) {
      salePointClient
        .getById(order.destination.knownId)
        .then((response) => {
          setDestination(response);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [order, salePointClient]);

  useEffect(() => {
    if (!order) {
      return;
    }

    setIsWaitingForPayment(order.status === OrderStatus.WaitingForPayment);

    setIsPaid(!isWaitingForPayment && order.status !== OrderStatus.Cancelled && order.payment?.paidOn !== undefined);
  }, [order, isWaitingForPayment]);

  const getInfoItem = (name: string, value?: string | React.JSX.Element) => {
    return (
      value && (
        <Box sx={{ mt: '5px' }}>
          <InfoItemValue>{value}</InfoItemValue>
          <InfoItemName>{name}</InfoItemName>
        </Box>
      )
    );
  };

  const isProduct = (item: OrderItem): boolean => item.isItem && !item.isPack;

  const getLocalDate = (date: moment.Moment) => {
    if (!date) {
      return '-';
    }

    const localDate = moment.utc(date).local().locale('lt');
    if (localDate.year() === 1) {
      return '-';
    }

    return localDate.format('YYYY/MM/DD');
  };

  const continuePayment = () => {
    payForOrder(order.id);
  };

  const payForOrder = async (orderIdToProcess: string) => {
    setPaymentStatus(PaymentStatus.Loading);
    setOpenPaymentDialog(true);
    getOrderInfo(orderIdToProcess);
  };

  const getOrderInfo = (orderIdToProcess: string) => {
    orderClient
      .get2(orderIdToProcess, userInfo.id)
      .then((response) => {
        checkForStatus(response);
      })
      .catch((error) => {
        setPaymentStatus(PaymentStatus.Failure);
        console.log(error);
      });
  };

  const checkForStatus = (orderInfo: Order) => {
    if (orderInfo.status === OrderStatus.WaitingForPayment) {
      openCheckout(orderInfo.payment.checkoutUrl);
      waitForPayment(orderInfo.id, orderInfo.payment.isActive);
      assignCustomerDetails();
    } else {
      setTimeout(function () {
        getOrderInfo(orderInfo.id);
      }, 1500);
    }
  };

  const openCheckout = (url: string) => {
    if (window.cordova) {
      inAppBrowserRef = window.cordova.InAppBrowser.open(url, '_blank', 'location=no');

      exitListenerRef.current = () => {
        handlePaymentFailure();
      };

      loadStopListenerRef.current = (event: any) => {
        const currentUrl = event.url.toLowerCase();
        if (currentUrl.includes('/failure')) {
          handlePaymentFailure();
        }
      };

      inAppBrowserRef.addEventListener('exit', exitListenerRef.current);
      inAppBrowserRef.addEventListener('loadstop', loadStopListenerRef.current);
    } else {
      if (appStyleMode === AppStyle.Mobile) {
        window.open(url, '_blank');
      } else {
        window.location.replace(url);
      }
    }
  };

  const handlePaymentFailure = () => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = 0;
    }
    loadPaymentInfo(PaymentStatus.Failure);
  };

  function waitForPayment(orderIdToWait: string, isActivated: boolean) {
    let retryCount = 0;
    const MAX_RETRIES = 300;

    async function continueWaiting() {
      if (paymentTimeoutRef.current) {
        clearTimeout(paymentTimeoutRef.current);
        paymentTimeoutRef.current = 0;
      }

      if (retryCount >= MAX_RETRIES) {
        console.error('Payment timeout: Maximum retries exceeded');
        loadPaymentInfo(PaymentStatus.Failure);
        return;
      }

      retryCount++;

      try {
        const payment = await orderClient.getPayment(orderIdToWait, userInfo.id);

        if (!isActivated) {
          isActivated = payment.isActive;
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        if (payment.isActive) {
          paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
          return;
        }

        if (payment.paidOn) {
          loadPaymentInfo(PaymentStatus.Success);
        } else {
          loadPaymentInfo(PaymentStatus.Failure);
        }
      } catch (error) {
        console.error('Error fetching payment info:', error);
        paymentTimeoutRef.current = setTimeout(continueWaiting, 2000) as any;
      }
    }

    continueWaiting();
  }

  const loadPaymentInfo = (status: PaymentStatus) => {
    setPaymentStatus(status);
    setOpenPaymentDialog(true);

    if (window.cordova && inAppBrowserRef) {
      if (exitListenerRef.current) {
        inAppBrowserRef.removeEventListener('exit', exitListenerRef.current);
        exitListenerRef.current = null;
      }
      if (loadStopListenerRef.current) {
        inAppBrowserRef.removeEventListener('loadstop', loadStopListenerRef.current);
        loadStopListenerRef.current = null;
      }

      setTimeout(function () {
        inAppBrowserRef.close();
      }, 4000);
    }
  };

  const closePaymentDialog = () => {
    setOpenPaymentDialog(false);
    if (paymentStatus === PaymentStatus.Success) {
      assignCustomerDetails();
      // Reload order to reflect paid status
      orderClient.get2(order.id, userInfo.id).then(setOrder).catch(console.error);
    }
  };

  const cancelPayment = async () => {
    try {
      setIsBusy(true);
      await orderClient.reject(customerInfo.id, order.id);
      const updatedOrder = await orderClient.get2(order.id, customerInfo.id);
      setOrder(updatedOrder);
      // refresh customer details if order was paid with credits
      if (order.creditsUsed > 0) {
        assignCustomerDetails();
      }
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('order:order', { orderNumber: order?.number })} />
      <BreadcrumbsBar
        paths={[
          { path: OrderHistoryRoute, isLink: true },
          { path: OrderDetailRoute, title: order?.number },
        ]}
      />
      <PaymentDialog
        open={openPaymentDialog}
        onClose={closePaymentDialog}
        paymentStatus={paymentStatus}
        autoCloseDelay={5000}
      />
      {order && (
        <>
          <OrderDetailsContainer
            isMobile={appStyleMode === AppStyle.Mobile}
            className={appStyleMode === AppStyle.Mobile && 'order-details'}
          >
            <Box sx={{ mx: '15px', pt: '10px' }}>
              <Card sx={{ marginBottom: '20px', margin: '5px' }}>
                <CardHeader
                  sx={{ backgroundColor: 'card.primary.background', color: 'card.primary.color', textAlign: 'center' }}
                  title={<HeaderText>{order?.number + ' ' + t('order:summary')}</HeaderText>}
                />
                <CardContent sx={{ paddingTop: '5px' }}>
                  <Box>
                    <OrderStatusStepper
                      status={order.status}
                      deliveryType={OrderHelper.getDeliveryType(order)}
                      isOnlyPhysicalItems={isItemsOnly}
                    />
                    {order &&
                      (order.status === OrderStatus.Cancelled || order.status === OrderStatus.ProcessingPayment) && (
                        <UnpaidAreaContainer>
                          <Label
                            type="error"
                            content={getOrderStatusLabel(order.status, isItemsOnly, OrderHelper.getDeliveryType(order))}
                          />
                        </UnpaidAreaContainer>
                      )}
                    {order && !isWaitingForPayment && order.status !== OrderStatus.Cancelled && (
                      <Box>
                        {destination && (
                          <Box>
                            {getInfoItem(
                              t('order:destinationAddress'),
                              `${destination.name} ${destination.address} ${destination.city}`
                            )}
                            {getInfoItem(t('order:destinationPhone'), <Phone phone={destination.phone} />)}
                          </Box>
                        )}
                        {
                          <DetailDatesBox>
                            {getInfoItem(t('order:orderDate'), getLocalDate(order?.createdOn))}
                            {getInfoItem(t('order:plannedEndDate'), getLocalDate(order?.returnDate))}
                          </DetailDatesBox>
                        }
                        {order?.destination?.parcelLocker ? (
                          <Box sx={{ mt: '5px' }}>
                            <InfoItemValue>{order.destination.parcelLocker.provider}</InfoItemValue>
                            <InfoItemValue>{order.destination.address.fullAddress}</InfoItemValue>
                            <InfoItemName>{t('order:parcelLockerAddress')}</InfoItemName>
                          </Box>
                        ) : (
                          order?.destination?.address &&
                          getInfoItem(t('order:deliveryAddress'), order.destination.address.fullAddress)
                        )}
                      </Box>
                    )}
                    {order && isWaitingForPayment && (
                      <UnpaidAreaContainer>
                        <Label type="error" content={t('order:notPaid')} />
                        <Box sx={{ textAlign: 'center' }}>
                          <CancelPaymentButton disabled={isBusy} onClick={cancelPayment}>
                            {t('order:cancel')}
                          </CancelPaymentButton>
                          <ConfirmPaymentButton disabled={isBusy} onClick={continuePayment}>
                            {t('order:pay')}
                          </ConfirmPaymentButton>
                        </Box>
                      </UnpaidAreaContainer>
                    )}
                    {orderServices?.length > 0 && (
                      <OrderItemsGroup header={t('order:orderServices')} items={orderServices} />
                    )}
                    {orderProducts?.length > 0 && (
                      <OrderItemsGroup header={t('order:orderProducts')} items={orderProducts} />
                    )}
                  </Box>

                  {order.totalDiscount !== 0 && (
                    <>
                      <Separator />
                      <DiscountTextTypography>
                        {t('order:discountText', { discount: order.totalDiscount?.toPrice() })}
                      </DiscountTextTypography>
                    </>
                  )}

                  <Separator />
                  <Box sx={{ textAlign: 'right', mt: '5px' }}>
                    <Typography sx={{ fontSize: '17px' }}>
                      {t('order:total', { total: order.totalPrice.toPrice() })}
                    </Typography>
                    {order.creditsUsed > 0 && (
                      <>
                        <Typography sx={{ fontSize: '17px' }}>
                          {t('order:credits', { credits: order.creditsUsed.toPrice() })}
                        </Typography>
                      </>
                    )}

                    <Typography sx={{ fontWeight: 'bold' }}>
                      {t('order:paidTotal', {
                        paid: isPaid ? (order.totalPrice - order.creditsUsed).toPrice() : (0).toPrice(),
                      })}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </OrderDetailsContainer>
        </>
      )}
      <Toolbar />
      {appStyleMode === AppStyle.Mobile && <BottomCartBar />}
    </Box>
  );
}
